/**
 * UsageScreen — painel de Custos da IA.
 *
 * Mostra o consumo real do tenant (app.wallet_usage): custo e tokens totais +
 * breakdown por MODELO, por OPERAÇÃO (chat/embed) e as EXECUÇÕES mais caras.
 * "Custo por agente" (SPEC-AGENTES-A4 FR-16): agente, custo, tokens e participação,
 * com filtro por execução (GET /dashboard/usage?execution_id=).
 * Dados via GET /dashboard/usage (budget_costs:view). Somente leitura.
 *
 * Cada recorte tem o gráfico de PARTICIPAÇÃO no visual do Dashboard (rosca + barras +
 * legenda), animado do zero até o valor quando entra na tela (pedido do dono, 2026-10-03).
 */
import { useState } from 'react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useUsageSummary } from '@/api/usage';
import { ParticipationChart } from '@/ui/sphere/ParticipationChart';

function fmtMoney(v, currency = 'USD') {
  const n = Number(v || 0);
  const sym = currency === 'BRL' ? 'R$' : currency === 'USD' ? 'US$' : `${currency} `;
  return `${sym} ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtTokens(n) {
  n = Number(n || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace('.', ',')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace('.', ',')}k`;
  return String(n);
}

function Kpi({ label, value, sub }) {
  return (
    <div style={{
      flex: '1 1 180px', background: C.blackElev, border: `1px solid ${C.blackBorder}`,
      borderRadius: 12, padding: '18px 20px',
    }}>
      <div style={{ fontSize: 12, letterSpacing: '.04em', textTransform: 'uppercase', color: C.neutralMuted }}>
        {label}
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color: C.text, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: 13, color: C.neutralDim, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

/**
 * Rótulo de uma execução na tela: projeto · início · começo do id (pedido do dono,
 * 2026-10-06 — a lista só trazia o UUID). O mesmo projeto roda várias vezes, então a data
 * e o id curto distinguem as execuções. Sem nome (execução excluída, consumo de
 * indexação), vale o id inteiro.
 */
export function rotuloDaExecucao(exec) {
  const id = String(exec?.key || exec?.label || '');
  const nome = String(exec?.name || '').trim();
  if (!nome) return id;
  const partes = [nome];
  const inicio = exec?.startedAt ? new Date(exec.startedAt) : null;
  if (inicio && !Number.isNaN(inicio.getTime())) {
    partes.push(inicio.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', ''));
  }
  partes.push(id.slice(0, 8));
  return partes.join(' · ');
}

/** Itens de um recorte da API → itens do gráfico de participação. */
function paraParticipacao(items, rotulo = null) {
  return (items || []).map((it, i) => ({
    key: `${it.key || it.label || 'item'}-${i}`,
    label: (rotulo ? rotulo(it) : '') || it.key || it.label || SEM_AGENTE,
    valor: Number(it.cost || 0),
    detalhe: `${it.calls || 0} chamada(s) · ${fmtTokens(it.totalTokens)} tokens`
      + ` (${fmtTokens(it.inputTokens)} in / ${fmtTokens(it.outputTokens)} out)`,
  }));
}

function fmtPct(parte, total) {
  if (!(total > 0)) return '—';
  const v = (Number(parte || 0) / total) * 100;
  return `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

const SEM_AGENTE = '(sem agente)';

const th = {
  textAlign: 'left', fontSize: 12, fontWeight: 600, letterSpacing: '.04em',
  textTransform: 'uppercase', color: C.neutralMuted, padding: '8px 10px',
  borderBottom: `1px solid ${C.blackBorder}`,
};
const td = {
  fontSize: 14, color: C.text, padding: '10px 10px', borderBottom: `1px solid ${C.blackBorder}`,
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Quadro "Custo por agente". O filtro por execução refaz a consulta só deste quadro;
 * as opções são as execuções mais caras do tenant (a lista diz quantas são), e um campo
 * aceita o ID de qualquer outra execução.
 */
export function CustoPorAgente({ geral, execucoes = [] }) {
  const [execucao, setExecucao] = useState('');
  const [idDigitado, setIdDigitado] = useState('');
  const filtrado = useUsageSummary(execucao || null);
  const dados = execucao ? filtrado.data : geral;
  const currency = (dados && dados.currency) || (geral && geral.currency) || 'USD';
  const itens = (dados && dados.byAgent) || [];
  const total = itens.reduce((acc, it) => acc + Number(it.cost || 0), 0);

  const aplicarId = () => setExecucao(idDigitado.trim());

  return (
    <div style={{ marginTop: 8 }} data-testid="custo-por-agente">
      <h3 style={sectionTitle}>Custo por agente</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
        <label style={{ color: C.neutralDim, fontSize: 13 }} htmlFor="filtro-execucao-agente">
          Execução
        </label>
        <select
          id="filtro-execucao-agente"
          aria-label="Filtrar por execução"
          value={execucoes.some((e) => e.label === execucao) ? execucao : ''}
          onChange={(e) => { setExecucao(e.target.value); setIdDigitado(''); }}
          style={{
            background: C.blackElev, color: C.text, border: `1px solid ${C.blackBorder}`,
            borderRadius: 8, padding: '6px 10px', fontSize: 13, maxWidth: '100%',
          }}
        >
          <option value="">Todas as execuções</option>
          {execucoes.map((e) => (
            <option key={e.label} value={e.label} title={e.label}>
              {rotuloDaExecucao(e)} — {fmtMoney(e.cost, currency)}
            </option>
          ))}
        </select>
        <input
          aria-label="ID da execução"
          placeholder="ou cole o ID da execução"
          value={idDigitado}
          onChange={(e) => setIdDigitado(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') aplicarId(); }}
          style={{
            background: C.blackElev, color: C.text, border: `1px solid ${C.blackBorder}`,
            borderRadius: 8, padding: '6px 10px', fontSize: 13, minWidth: 220, flex: '1 1 220px',
          }}
        />
        <button
          type="button"
          onClick={aplicarId}
          style={{
            background: 'transparent', color: C.accent, border: `1px solid ${C.blackBorder}`,
            borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Filtrar
        </button>
      </div>
      {execucoes.length > 0 ? (
        <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 8 }}>
          A lista traz as {execucoes.length} execuções mais caras; para outra, cole o ID.
        </div>
      ) : null}

      {itens.length > 0 && !(execucao && (filtrado.isLoading || filtrado.isError)) ? (
        <ParticipationChart titulo="Participação por agente" itens={paraParticipacao(itens)}
          formatar={(n) => fmtMoney(n, currency)} testId="grafico-por-agente" />
      ) : null}

      {execucao && filtrado.isLoading ? (
        <div style={{ color: C.neutralMuted, fontSize: 14 }}>Carregando…</div>
      ) : execucao && filtrado.isError ? (
        <div style={{ color: C.neutralMuted, fontSize: 14 }}>
          Não foi possível carregar o custo desta execução.
        </div>
      ) : itens.length === 0 ? (
        <div style={{ color: C.neutralMuted, fontSize: 14, padding: '8px 2px' }}>
          {execucao ? 'Nenhum consumo registrado para esta execução.' : 'Sem dados ainda.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: C.blackElev, borderRadius: 10 }}>
            <thead>
              <tr>
                <th style={th}>Agente</th>
                <th style={{ ...th, textAlign: 'right' }}>Custo</th>
                <th style={{ ...th, textAlign: 'right' }}>Tokens</th>
                <th style={{ ...th, textAlign: 'right' }}>Participação</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it) => {
                const nome = it.key || it.label || SEM_AGENTE;
                return (
                  <tr key={nome} data-testid="linha-agente">
                    <td style={{ ...td, color: nome === SEM_AGENTE ? C.neutralMuted : C.text, wordBreak: 'break-all' }}>
                      {nome}
                      <div style={{ color: C.neutralMuted, fontSize: 12 }}>{it.calls} chamada(s)</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(it.cost, currency)}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {Number(it.totalTokens || 0).toLocaleString('pt-BR')}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtPct(it.cost, total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...td, fontWeight: 600 }}>Total</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(total, currency)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {itens.reduce((a, it) => a + Number(it.totalTokens || 0), 0).toLocaleString('pt-BR')}
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{total > 0 ? '100%' : '—'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const sectionTitle = {
  fontSize: 13, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
  color: C.neutralDim, margin: '20px 0 10px',
};

export function UsageScreen() {
  const { data, isLoading, isError, error, refetch } = useUsageSummary();

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <QueryError error={error} onRetry={refetch} />;

  const u = data || {};
  const currency = u.currency || 'USD';
  const byModel = u.byModel || [];
  const byOperation = u.byOperation || [];
  const topExecutions = u.topExecutions || [];

  return (
    // Largura toda da área útil, só com as margens laterais (pedido do dono, 2026-10-06):
    // o teto de 1040 px deixava metade de um monitor largo vazia.
    <div data-testid="tela-de-custos" style={{ padding: '24px clamp(16px, 4vw, 40px)' }}>
      {/* KPIs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        <Kpi label="Custo total" value={fmtMoney(u.totalCost, currency)} sub={`${u.totalCalls || 0} chamadas de IA`} />
        <Kpi label="Tokens totais" value={fmtTokens(u.totalTokens)} sub={`${fmtTokens(u.inputTokens)} in / ${fmtTokens(u.outputTokens)} out`} />
        <Kpi label="Entrada" value={fmtTokens(u.inputTokens)} sub="tokens de prompt" />
        <Kpi label="Saída" value={fmtTokens(u.outputTokens)} sub="tokens gerados" />
      </div>

      {(u.totalCalls || 0) === 0 ? (
        <div style={{ color: C.neutralMuted, fontSize: 14, marginTop: 24 }}>
          Nenhum consumo de IA registrado ainda. Rode uma execução ou uma indexação e os custos aparecem aqui.
        </div>
      ) : (
        <>
          <CustoPorAgente geral={u} execucoes={topExecutions} />
          <ParticipationChart titulo="Por modelo" itens={paraParticipacao(byModel)}
            formatar={(n) => fmtMoney(n, currency)} testId="grafico-por-modelo" />
          <ParticipationChart titulo="Por operação" itens={paraParticipacao(byOperation)}
            formatar={(n) => fmtMoney(n, currency)} testId="grafico-por-operacao" />
          <ParticipationChart titulo="Execuções mais caras"
            itens={paraParticipacao(topExecutions, rotuloDaExecucao)}
            formatar={(n) => fmtMoney(n, currency)} testId="grafico-execucoes" />
        </>
      )}
    </div>
  );
}

export default UsageScreen;
